import Flex from "@ui-kit/layout/Flex/Flex.web";
import { Switch as ChakraSwitch } from "@chakra-ui/react";
import React from "react";
import Text from "@ui-kit/typography/Text/Text.web";
import { SwitchSharedProps } from "./Switch.props";

export type SwitchProps = SwitchSharedProps & {
  labelFontWeight?: string;
};

const Switch = ({ "data-testid": dataTestId, ...props }: SwitchProps) => {
  const {
    label,
    subtitle,
    labelFontWeight = "normal",
    isChecked,
    checked,
    isDisabled,
    disabled,
    onChange,
    isReadOnly,
    readOnly,
    colorPalette = "primary",
    children,
    ...rest
  } = props;

  // Map v1/v2 props to v3 props for backward compatibility
  const checkedValue = checked !== undefined ? checked : isChecked;
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const readOnlyValue = readOnly !== undefined ? readOnly : isReadOnly;

  let resolvedLabel = label;
  if (resolvedLabel === undefined && typeof children === "string") {
    resolvedLabel = children;
  }

  // Map colorPalette to the checked background color
  const getCheckedBg = (): string => {
    switch (colorPalette) {
      case "blue":
        return "blue.solid";
      case "green":
        return "green.solid";
      case "red":
        return "red.solid";
      case "grey":
        return "grey.solid";
      default:
        return "primary.solid";
    }
  };

  const switchElement = (
    <ChakraSwitch.Root
      data-testid={dataTestId}
      display="flex"
      alignItems="center"
      colorPalette={colorPalette}
      checked={checkedValue}
      disabled={disabledValue}
      readOnly={readOnlyValue}
      onCheckedChange={(details: { checked: boolean }) => {
        if (onChange) {
          const event = {
            target: { checked: details.checked },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(event);
        }
      }}
      {...rest}
    >
      <ChakraSwitch.HiddenInput />
      <ChakraSwitch.Control bg="gray.300" _checked={{ bg: getCheckedBg() }}>
        <ChakraSwitch.Thumb />
      </ChakraSwitch.Control>
    </ChakraSwitch.Root>
  );

  if (resolvedLabel || subtitle)
    return (
      <Flex
        direction="row"
        justify="space-between"
        gap="md"
        alignItems="center"
      >
        <Flex direction="column">
          {resolvedLabel && (
            <Text
              fontSize="sm"
              fontWeight={labelFontWeight}
              color="fg"
              wordBreak="break-all"
            >
              {resolvedLabel}
            </Text>
          )}

          {subtitle && (
            <Text fontSize="sm" color="fg.muted" wordBreak="break-all">
              {subtitle}
            </Text>
          )}
        </Flex>
        {switchElement}
      </Flex>
    );

  return switchElement;
};

Switch.Bold = function SwitchBold(props: SwitchProps): JSX.Element {
  return <Switch {...props} labelFontWeight="700" />;
};

Switch.SubtitleOnly = function SwitchSubtitleOnly(
  props: SwitchProps
): JSX.Element {
  return <Switch {...props} label={undefined} />;
};

export default Switch;
